import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { IReportingQuery } from '@dadohr/shared/domainTypes/reports';
import { Typography } from '@material-ui/core';
import { CheckCircle2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';

import { Loader } from '../../../../components/Loader';
import { SomethingWentWrong } from '../../../../components/SomethingWentWrong';
import { TeaserBoxFreeTierForExperiences } from '../../../../components/TeaserBox';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { useTrackDuration } from '../../../../hooks/useTrackDuration';
import { useUnsafeTranslation } from '../../../../i18next';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../services/currentUser';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { CompletionPerTask } from '../../components/CompletionPerTask';
import {
  EmptyStateNoInstances,
  EmptyStateNoReportableTemplates
} from '../../components/EmptyState';
import { ReportCard } from '../../components/ReportCard';
import { ReportExperienceCompletionHeatmap } from '../../components/ReportExperienceCompletionHeatmap';
import { ReportFilters } from '../../components/ReportFilters';
import { TasksBlockBarChart } from '../../components/TasksBlockBarChart';
import { TotalTasks } from '../../components/TotalTasks';
import { useReportsSideNav } from '../../layout';
import {
  parseReportingQueryParams,
  useDefaultReportingQueryParams,
  useReportsTasksDataViaCf
} from '../../services/data';

const ReportsTasksBody = ({
  q,
  setQ,
  defaultQ
}: {
  q: IReportingQuery;
  setQ: (nextQ: IReportingQuery | null) => void;
  defaultQ: IReportingQuery | void;
}) => {
  const { t } = useUnsafeTranslation();

  const [data, loading, error] = useReportsTasksDataViaCf(q);
  // const [data, loading, error] = useReportsTasksData(q);
  const { trackEvent } = useProductAnalytics();
  const { startTimer, getDuration } = useTrackDuration();
  useErrorLogger('reportsOverviewData', error);

  const tplId = data?.q.tplIds[0];
  useEffect(() => {
    startTimer();
    if (tplId) {
      trackEvent({
        eventType: PAEventType.EXPERIENCE_TPL,
        name: 'Tasks Report Page Loaded',
        'Experience Template ID': tplId,
        Explanation: 'Admin user loaded the Tasks report page'
      });
    }

    return () => {
      if (tplId) {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Tasks Report Page Exited',
          Explanation: 'Admin User loaded the Tasks report page',
          'Time On Page': Math.round(getDuration() / 1000)
        });
      }
    };
  }, [trackEvent, tplId, getDuration, startTimer]);

  return (
    <>
      <Section spacing={4}>
        <ReportFilters
          value={q}
          onChange={setQ}
          initialValue={defaultQ}
          additionalTrackingProps={(ev) => ({
            ...ev,
            Report: 'Tasks'
          })}
        />
      </Section>
      {loading && <Loader height={500} />}
      {error && <SomethingWentWrong height={500} />}
      {data &&
        (data.data.instances.count ? (
          <>
            <Section spacing={4}>
              <ReportCard
                icon={({ size, colors }) => (
                  <CheckCircle2 size={size} color={colors.blue.w500} />
                )}
                title={t('Tasks')}
              >
                <Section spacing={8}>
                  <TotalTasks
                    data={data.data.totals}
                    actors={data.actors}
                    title="tasks"
                  />
                </Section>
                <Section spacing={8}>
                  <TasksBlockBarChart
                    showActorFilter={true}
                    data={data.data.totals}
                    actors={data.actors}
                  />
                </Section>
                <Section spacing={8}>
                  <CompletionPerTask
                    actors={data.actors}
                    data={data.data.byTask}
                    experienceTitle={data.tpl.data.title}
                    tpl={data.tpl}
                  />
                </Section>
                <Section spacing={8}>
                  <ReportExperienceCompletionHeatmap
                    actors={data.actors}
                    data={data.data.byCohort}
                    tpl={data.tpl}
                  />
                </Section>
              </ReportCard>
            </Section>
          </>
        ) : (
          <EmptyStateNoInstances />
        ))}
    </>
  );
};

export const PageReportsTasks = () => {
  const { t } = useUnsafeTranslation();
  const sideNav = useReportsSideNav();
  const { ROUTES, goTo } = useRoutes();
  const [queryParam] = useStringQueryParam('q', '');
  const q = useMemo(() => parseReportingQueryParams(queryParam), [queryParam]);
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const currentUser = useCurrentUser();
  const [defaultQ, loadingQ, errorDefaultQ] = useDefaultReportingQueryParams(
    currentUser
  );
  useErrorLogger('default q', errorDefaultQ);

  const setQ = (nextQ: IReportingQuery | null) =>
    goTo(ROUTES.reports.tasks.url(nextQ));

  const actualQ = q || defaultQ;

  const { startTimer, getDuration } = useTrackDuration();
  const { trackEvent } = useProductAnalytics();

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startTimer();
      } else {
        trackEvent({
          eventType: PAEventType.OTHER,
          name: 'Tasks Report Page Exited',
          Explanation: 'Admin User loaded the Tasks page',
          'Time On Page': Math.round(getDuration() / 1000)
        });
      }
    };

    const handleWindowClose = (e: any) => {
      e.preventDefault();
      trackEvent({
        eventType: PAEventType.OTHER,
        name: 'Tasks Report Page Exited',
        Explanation: 'Admin User loaded the Tasks page',
        'Time On Page': Math.round(getDuration() / 1000)
      });
    };
    startTimer();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleWindowClose);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleWindowClose);
    };
  });

  // TODO check if user can view the requested tpls
  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <TeaserBoxFreeTierForExperiences>
        {t(
          'This is a preview of the reporting Dado offers for Experiences, using sample data.'
        )}
      </TeaserBoxFreeTierForExperiences>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('Tasks report')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      {!actualQ && loadingQ && <Loader height={500} />}
      {actualQ &&
        defaultQ &&
        (actualQ.tplIds.length ? (
          <ReportsTasksBody q={actualQ} setQ={setQ} defaultQ={defaultQ} />
        ) : (
          <EmptyStateNoReportableTemplates />
        ))}
    </PageBody>
  );
};
